import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export type ProductStatus = 'draft' | 'active' | 'archived' | 'discontinued'
export type PricingType = 'one_time' | 'subscription'
export type BillingPeriod = 'monthly' | 'quarterly' | 'yearly' | 'lifetime'
export type PaymentMethod = 'credit_card' | 'bank_transfer' | 'virtual_account' | 'mobile' | 'paypal' | 'stripe' | 'other'
export type PaymentStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded' | 'partial_refund'
export type OrderStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded'

export interface PricingResponse {
  id: number
  name: string
  pricingType: PricingType
  billingPeriod: BillingPeriod
  billingPeriodMonths: number | null
  price: number
  currency: string
  originalPrice: number | null
  discountPercentage: number | null
  maxSeats: number
  hasTrial: boolean
  trialDays: number
  isDefault: boolean
}

export interface ProductResponse {
  id: number
  name: string
  slug: string | null
  shortDescription: string | null
  description: string | null
  thumbnailUrl: string | null
  bannerUrl: string | null
  status: ProductStatus
  curriculumContentId: number | null
  curriculumTitle: string | null
  contents: ProductContentResponse[]
  pricingOptions: PricingResponse[]
  createdAt: string | null
}

export interface ProductContentResponse {
  contentId: number
  title: string
  customTitle: string | null
  sequenceOrder: number
}

export interface CurriculumProductInfo {
  curriculumId: number
  productId: number | null
  productName: string | null
  hasProduct: boolean
  pricingOptions: PricingResponse[]
  defaultPrice: number | null
  defaultPricingId: number | null
  currency: string
}

export interface CreateOrderRequest {
  productId: number
  pricingId: number
  quantity?: number
  couponCode?: string
  billingName?: string
  billingEmail?: string
  billingPhone?: string
  billingAddress?: string
}

export interface ProcessPaymentRequest {
  orderId: number
  paymentMethod: PaymentMethod
  cardNumber?: string
  cardExpiry?: string
  cardCvc?: string
}

export interface OrderItemResponse {
  id: number
  productId: number
  productName: string
  pricingName: string
  unitPrice: number
  quantity: number
  finalPrice: number
  seatsPurchased: number
}

export interface OrderResponse {
  id: number
  orderNumber: string
  orderStatus: OrderStatus
  subtotal: number
  discountAmount: number
  taxAmount: number
  totalAmount: number
  currency: string
  items: OrderItemResponse[]
  createdAt: string | null
  completedAt: string | null
}

export interface PaymentResponse {
  id: number
  orderId: number
  paymentMethod: PaymentMethod
  paymentStatus: PaymentStatus
  amount: number
  currency: string
  pgTransactionId: string | null
  paidAt: string | null
  createdAt: string | null
}

export interface CheckoutResponse {
  order: OrderResponse
  payment: PaymentResponse
  subscriptionId: number | null
  message: string
}

// ============================================================================
// API Functions
// ============================================================================

export const commerceApi = {
  /**
   * 모든 상품 목록 조회
   */
  getProducts: async (): Promise<ProductResponse[]> => {
    const response = await apiClient.get<ApiResponse<ProductResponse[]>>('/commerce/products')
    return response.data.data || []
  },

  /**
   * 상품 상세 조회
   */
  getProduct: async (productId: number): Promise<ProductResponse> => {
    const response = await apiClient.get<ApiResponse<ProductResponse>>(
      `/commerce/products/${productId}`
    )
    if (!response.data.data) {
      throw new Error('Product not found')
    }
    return response.data.data
  },

  /**
   * Curriculum의 상품 정보 조회
   */
  getProductByCurriculum: async (curriculumId: number): Promise<CurriculumProductInfo> => {
    const response = await apiClient.get<ApiResponse<CurriculumProductInfo>>(
      `/commerce/products/curriculum/${curriculumId}`
    )
    if (!response.data.data) {
      throw new Error('Product info not found')
    }
    return response.data.data
  },

  /**
   * 상품 가격 옵션 조회
   */
  getProductPricing: async (productId: number): Promise<PricingResponse[]> => {
    const response = await apiClient.get<ApiResponse<PricingResponse[]>>(
      `/commerce/products/${productId}/pricing`
    )
    return response.data.data || []
  },

  /**
   * 주문 생성
   */
  createOrder: async (request: CreateOrderRequest): Promise<OrderResponse> => {
    const response = await apiClient.post<ApiResponse<OrderResponse>>(
      '/commerce/orders',
      request
    )
    if (!response.data.data) {
      throw new Error('Failed to create order')
    }
    return response.data.data
  },

  /**
   * 결제 처리
   */
  processPayment: async (request: ProcessPaymentRequest): Promise<CheckoutResponse> => {
    const response = await apiClient.post<ApiResponse<CheckoutResponse>>(
      '/commerce/orders/pay',
      request
    )
    if (!response.data.data) {
      throw new Error('Failed to process payment')
    }
    return response.data.data
  },

  /**
   * 내 주문 목록 조회
   */
  getMyOrders: async (page = 0, size = 10): Promise<OrderResponse[]> => {
    const response = await apiClient.get<ApiResponse<OrderResponse[]>>(
      `/commerce/orders?page=${page}&size=${size}`
    )
    return response.data.data || []
  },

  /**
   * 주문 상세 조회
   */
  getOrder: async (orderNumber: string): Promise<OrderResponse> => {
    const response = await apiClient.get<ApiResponse<OrderResponse>>(
      `/commerce/orders/${orderNumber}`
    )
    if (!response.data.data) {
      throw new Error('Order not found')
    }
    return response.data.data
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

export function formatPrice(price: number, currency = 'KRW'): string {
  if (currency === 'KRW') {
    return `₩${price.toLocaleString()}`
  }
  return `$${price.toLocaleString()}`
}

export function getBillingPeriodLabel(billingPeriod: BillingPeriod): string {
  switch (billingPeriod) {
    case 'monthly':
      return '월간'
    case 'quarterly':
      return '분기'
    case 'yearly':
      return '연간'
    case 'lifetime':
      return '평생'
    default:
      return billingPeriod
  }
}

